<dsm-deal-incentive-pool-banner />
<div
  class="flex items-center flex-wrap gap-3 px-1 shrink-0"
  [class.pt-6]="hasExecutedRule() && !executedRulesBannerDismissed()"
>
  @if (hasExecutedRule() && !executedRulesBannerDismissed()) {
    <p-message class="w-full mb-2" severity="info" [closable]="true" (onClose)="executedRulesBannerDismissed.set(true)">
      <div class="flex flex-col gap-1.5 min-w-0">
        <span class="text-body-sm-600">Floor plan {{ allExecutions().length > 1 ? 'rules' : 'rule' }} executed</span>
        <ul class="m-0 p-0 list-none flex flex-col gap-1 text-body-xs">
          @for (execution of allExecutions(); track execution.executionId) {
            <li class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span class="font-semibold">{{ executionLabel(execution) }}:</span>
              <span class="line-through opacity-75">{{ roomNames(execution.replacedRooms) }}</span>
              <i class="pi pi-arrow-right" style="font-size: 0.65rem"></i>
              <span class="font-semibold">{{ roomNames(execution.newRooms) }}</span>
            </li>
          }
        </ul>
      </div>
    </p-message>
  }
  <div #actionsGroup class="flex items-center gap-2 flex-wrap absolute top-3 right-0 lg:right-4 z-10">
    @if (effectiveTargetDate()) {
      <p-button
        [outlined]="true"
        icon="pi pi-print"
        [label]="printAddendumsButtonLabel()"
        [disabled]="isDealOptionsLoading() || visibleDealsOptions().length === 0 || addendumsForSignOff().length === 0"
        (onClick)="openPrintAddendumDialog()"
        styleClass="max-2xl:!w-8 max-2xl:!h-8 max-2xl:!p-0 max-2xl:!rounded-full max-2xl:[&>.p-button-label]:!hidden"
      />
    } @else {
      <p-button
        icon="pi pi-print"
        label="Print Addendums"
        [disabled]="true"
        pTooltip="Target date is not set"
        tooltipPosition="top"
        variant="outlined"
        styleClass="max-2xl:!w-8 max-2xl:!h-8 max-2xl:!p-0 max-2xl:!rounded-full max-2xl:[&>.p-button-label]:!hidden"
      />
    }
    <p-button
      [outlined]="true"
      icon="pi pi-pen-to-square"
      [label]="sendForSignOffButtonLabel()"
      [disabled]="isDealOptionsLoading() || addendumsForSignOff().length === 0"
      (onClick)="openSendForSignOffDialog()"
      styleClass="max-2xl:!w-8 max-2xl:!h-8 max-2xl:!p-0 max-2xl:!rounded-full max-2xl:[&>.p-button-label]:!hidden"
    />
    @if (!isDealCancelled()) {
      <p-button
        icon="pi pi-plus"
        label="Add Addendum"
        (onClick)="openAddAddendumDialog()"
        styleClass="max-xl:!w-8 max-xl:!h-8 max-xl:!p-0 max-xl:!rounded-full max-xl:[&>.p-button-label]:!hidden"
      />
    }
  </div>
</div>

@if (replacements().length > 0) {
  <div class="flex flex-col gap-2 mb-3 shrink-0">
    @for (replacement of replacements(); track replacement.executionId) {
      <div class="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-red-700">
        <i class="pi pi-exclamation-circle text-body-sm"></i>
        <p class="text-body-xs m-0 flex-1">
          <span class="font-semibold">{{ replacement.triggerProductCode || 'A plan change' }}</span>
          was selected which replaced
          <span class="font-semibold">{{ roomNames(replacement.replacedRooms) }}</span>
          with
          <span class="font-semibold">{{ roomNames(replacement.newRooms) }}</span
          >. Options with mismatched locations are highlighted below — re-configure them to move them back to Addendums.
        </p>
      </div>
    }
  </div>
}

<div class="flex flex-1 overflow-hidden gap-4">
  <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
    <div class="flex flex-col flex-1 overflow-hidden mt-1">
      <div class="overflow-y-auto flex-1 flex flex-col gap-4 pt-3">
        @if (isDealOptionsLoading()) {
          <lib-skeleton [count]="3" [lines]="2" />
        } @else if (showConflictsOnly() && visibleDealsOptions().length === 0 && dealsOptions().length > 0) {
          <p class="text-body-sm text-surface-500 text-center py-8 m-0">No addendums with conflicts.</p>
        } @else {
          @for (dealAddendum of visibleDealsOptions(); track dealAddendum.id) {
            @let isEditingAddendum = dealAddendum.id === selectedAddendumId() && !selectedProductId();
            <div
              [attr.id]="'addendum-' + dealAddendum.id"
              (click)="onAddendumSelect(dealAddendum.id)"
              class="addendum-card border border-slate-200 rounded-md px-2 py-4 md:p-4 cursor-pointer relative shrink-0"
              [class.addendum-editing]="isEditingAddendum"
              [class.!border-red-300]="hasMismatch(dealAddendum.id)"
              [ngClass]="{ 'border-slate-400': dealAddendum.isExpanded }"
            >
              <span class="absolute -top-2 left-6 z-10 flex items-center gap-1.5 whitespace-nowrap">
                <p-badge
                  [value]="'#' + dealAddendum.seq"
                  severity="contrast"
                  size="small"
                  [class]="
                    isEditingAddendum
                      ? 'addendum-seq-badge addendum-seq-badge-editing shadow-sm'
                      : 'addendum-seq-badge shadow-sm'
                  "
                />
                @if (isEditingAddendum) {
                  <p-tag
                    value="Editing"
                    icon="pi pi-pencil"
                    size="small"
                    severity="contrast"
                    [rounded]="false"
                    styleClass="addendum-editing-tag shadow-sm"
                  />
                }
                @if (canViewPendingLocationRules() && dealAddendum.pendingLocationRules.length) {
                  <p-tag
                    value="Location rule(s) pending"
                    icon="pi pi-exclamation-triangle"
                    size="small"
                    severity="warn"
                    [rounded]="false"
                    styleClass="shadow-sm cursor-pointer"
                    (click)="$event.stopPropagation(); pendingRulesPopover.toggle($event)"
                  />
                  <p-popover #pendingRulesPopover appendTo="body">
                    <div class="flex flex-col gap-3 w-72">
                      <span class="text-body-sm font-semibold">Pending location rules</span>
                      <ul class="m-0 p-0 list-none flex flex-col gap-2 max-h-60 overflow-y-auto">
                        @for (rule of dealAddendum.pendingLocationRules; track rule.ruleId) {
                          <li class="flex flex-col">
                            <span class="text-body-sm font-medium">{{ rule.triggerProductCode }}</span>
                            <span class="text-body-xs text-surface-500">{{ rule.triggerProductDescription }}</span>
                          </li>
                        }
                      </ul>
                      <p-button
                        label="Apply location rule(s)"
                        size="small"
                        styleClass="w-full"
                        [disabled]="!addendumPreview.canRerunRoomReplacementRules()"
                        (onClick)="pendingRulesPopover.hide(); addendumPreview.openRoomReplacementRerunModal()"
                      />
                    </div>
                  </p-popover>
                }
                @if (hasMismatch(dealAddendum.id)) {
                  <p-badge
                    value="Mismatch locations in Options"
                    severity="danger"
                    size="small"
                    class="addendum-mismatch-badge shadow-sm"
                  />
                }
              </span>
              <div class="flex items-start gap-2.5 pr-0 md:pr-24">
                <p-checkbox
                  binary="true"
                  [ngModel]="isAddendumSelectedForPrint(dealAddendum.id)"
                  (onChange)="onAddendumPrintToggle(dealAddendum.id, $event.checked)"
                  (click)="$event.stopPropagation()"
                  [attr.aria-label]="'Select addendum ' + dealAddendum.seq + ' for printing'"
                ></p-checkbox>
                <dsm-aps-addendum-preview
                  #addendumPreview
                  (click)="$event.stopPropagation(); onAddendumSelect(dealAddendum.id)"
                  class="flex-1 min-w-0 flex items-start"
                  [dealAddendum]="dealAddendum"
                  [isExpanded]="dealAddendum.isExpanded"
                  [showSeq]="false"
                  (toggleExpand)="dealAddendum.isExpanded = !dealAddendum.isExpanded"
                  (onChanges)="onOptionChanges()"
                />
              </div>

              @if (dealAddendum.isExpanded) {
                <p-divider />
                <dsm-aps-addendum-options
                  [selectedProductId]="selectedProductId()"
                  [dealAddendum]="dealAddendum"
                  [showAddOption]="!isDealCancelled()"
                  [projectName]="effectiveDeal()?.project?.projectName ?? effectiveDeal()?.project?.projectCode ?? null"
                  [lotNo]="effectiveDeal()?.lot?.lotNo"
                  [modelId]="effectiveModelId()"
                  [elevationId]="effectiveElevationId()"
                  [modelName]="effectiveDeal()?.model?.modelName ?? null"
                  [elevationLabel]="effectiveDeal()?.elevation?.elevationCode ?? null"
                  [buyerName]="effectiveDeal()?.prospect?.name ?? null"
                  [executedTriggerProductIds]="executedTriggerProductIds()"
                  (onChanges)="onOptionCardChanges()"
                  (onProductSelected)="onAddendumProductSelect(dealAddendum.id, $event)"
                  (onKitProductSelected)="onAddendumProductSelect(dealAddendum.id, $event, true)"
                />
              }
            </div>
          }
        }
      </div>
    </div>
  </div>

  @if (showAddendumDetails() || showOptionDetails()) {
    <div
      [class]="
        isMobile()
          ? 'fixed inset-x-3 bottom-3 top-14 z-50 overflow-hidden rounded-xl'
          : 'w-80 xl:w-96 shrink-0 overflow-y-auto overflow-x-hidden self-start h-full'
      "
    >
      @if (showAddendumDetails()) {
        <dsm-aps-addendum-details
          [dealAddendum]="selectedAddendum()!"
          [dealCancelled]="isDealCancelled()"
          [isUnderContract]="isUnderContract()"
          (onChanges)="onOptionChanges()"
          (onClose)="closeMobileDetails()"
        />
      }
      @if (showOptionDetails()) {
        <dsm-aps-addendum-option-details
          [dealAddendum]="selectedAddendum()!"
          [addendumProductId]="selectedProductId()!"
          (onChanges)="onOptionChanges()"
          [isKitLinkedProduct]="selectedKitProductId === selectedProductId()"
          (onClose)="closeMobileDetails()"
        />
      }
    </div>
    @if (isMobile()) {
      <div class="fixed inset-0 bg-black/40 z-40" (click)="closeMobileDetails()"></div>
    }
  }
</div>

<p-dialog
  header="Floor plan rule executed"
  [visible]="currentDisclosure() !== null"
  [modal]="true"
  [closable]="false"
  [dismissableMask]="false"
  [draggable]="false"
  appendTo="body"
  styleClass="floor-plan-rule-dialog"
  [style]="{ width: 'min(92vw, 34rem)' }"
>
  @if (currentDisclosure(); as execution) {
    <div class="flex flex-col gap-4 pt-1">
      <p class="m-0 text-sm text-surface-600">A room replacement was applied to this floor plan.</p>
      <div class="flex items-center gap-3 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3">
        <i class="pi pi-box text-surface-500"></i>
        <div class="flex flex-col min-w-0">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-surface-500">Triggered by</span>
          <span class="font-semibold text-surface-800 break-words">{{ currentDisclosureLabel() }}</span>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-stretch gap-3">
        <div class="flex flex-col gap-2 rounded-lg border border-red-200 bg-red-50/60 px-4 py-3">
          <span class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-red-600">
            <i class="pi pi-minus-circle" style="font-size: 0.75rem"></i>
            Replaced
          </span>
          <div class="flex flex-wrap gap-1.5">
            @for (room of execution.replacedRooms; track room.locationId) {
              <span class="rounded-md bg-white border border-red-200 px-2 py-1 text-sm text-red-700 line-through">
                {{ room.locationName || '—' }}
              </span>
            } @empty {
              <span class="text-sm text-surface-500">—</span>
            }
          </div>
        </div>

        <div class="flex items-center justify-center text-surface-400">
          <i class="pi pi-arrow-right hidden sm:inline-block"></i>
          <i class="pi pi-arrow-down sm:hidden"></i>
        </div>

        <div class="flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3">
          <span class="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            <i class="pi pi-plus-circle" style="font-size: 0.75rem"></i>
            Now
          </span>
          <div class="flex flex-wrap gap-1.5">
            @for (room of execution.newRooms; track room.locationId) {
              <span
                class="rounded-md bg-white border border-emerald-200 px-2 py-1 text-sm font-medium text-emerald-800"
              >
                {{ room.locationName || '—' }}
              </span>
            } @empty {
              <span class="text-sm text-surface-500">—</span>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <ng-template pTemplate="footer">
    <p-button label="Acknowledge" icon="pi pi-check" (onClick)="acknowledgeCurrentDisclosure()" />
  </ng-template>
</p-dialog>

<p-dialog
  header="Add New Addendum"
  [visible]="addAddendumDialogVisible()"
  (visibleChange)="addAddendumDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '32rem' }"
  [appendTo]="'body'"
  [focusOnShow]="false"
>
  @if (addAddendumDialogVisible()) {
    <dsm-add-addendum-modal
      [lotId]="effectiveLotId()"
      [dealId]="effectiveDealId()"
      (saved)="onAddendumSaved()"
      (dialogClose)="addAddendumDialogVisible.set(false)"
    />
  }
</p-dialog>

<p-dialog
  [visible]="sendForSignOffDialogVisible()"
  (visibleChange)="sendForSignOffDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: 'min(94vw, 1024px)' }"
  [contentStyle]="{ overflow: 'hidden', maxHeight: '75vh', display: 'flex', flexDirection: 'column' }"
  [appendTo]="'body'"
>
  <ng-template pTemplate="header">
    <div class="flex items-center gap-3 flex-wrap min-w-0">
      <span class="font-semibold text-xl text-surface-700">Send for Sign-off</span>
      @if (hasBlockedAddendumsForSignOff()) {
        <span
          class="inline-flex items-center rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-body-2xs-700 text-red-700 uppercase tracking-wide"
        >
          Some addendums can't be sent
        </span>
      }
    </div>
  </ng-template>

  @if (sendForSignOffDialogVisible()) {
    <dsm-send-for-sign-off-modal
      class="flex flex-col flex-1 min-h-0"
      [dealId]="effectiveDealId()"
      [addendums]="addendumsForSignOff()"
      [lotNo]="effectiveDeal()?.lot?.lotNo ?? null"
      (sent)="onSentForSignOff()"
      (viewEnvelope)="onViewEnvelope($event)"
      (dialogClose)="sendForSignOffDialogVisible.set(false)"
    />
  }
</p-dialog>

<p-dialog
  header="Print Addendum Options"
  [visible]="printAddendumDialogVisible()"
  (visibleChange)="onPrintDialogVisibilityChange($event)"
  [modal]="true"
  [style]="{ width: '34rem' }"
  [appendTo]="'body'"
>
  <div class="flex flex-col gap-3">
    @for (option of printAddendumOptionsResource.value(); track option.id) {
      <label class="flex items-center gap-2">
        <p-checkbox
          [binary]="true"
          [ngModel]="isPrintOptionSelected(option.name)"
          (onChange)="onPrintOptionToggle(option.name, option.id, !!$event.checked)"
        />
        <span>{{ option.label }}</span>
      </label>
    }
    @if (printAddendumOptionsResource.value().length === 0) {
      <p class="m-0 text-sm text-gray-500">No print options found.</p>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-2">
      <p-button label="Cancel" [outlined]="true" (onClick)="onPrintDialogVisibilityChange(false)" />
      <p-button label="Print" (onClick)="submitPrintAddendums()" />
    </div>
  </ng-template>
</p-dialog>
